import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { C, NAV } from "../constants";
import SvgIcon from "../SvgIcon";
import { useBillingPermissions } from "../useBillingPermissions";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import { IPD_SIDEBAR } from "../../ipd/utils/ipdSidebar";

const SIDEBAR_W = 240;

// Dimmed color for disabled ("coming soon") nav items — no IPD_COLORS/IPD_SIDEBAR
// token matches this muted sidebar blue, left as a literal.
const DISABLED_NAV_COLOR = "#3d5e78";

// ─────────────────────────────────────────────────────────────────────────────
// SIDEBAR — position:fixed so it ignores all ancestor overflow values
// ─────────────────────────────────────────────────────────────────────────────
function Sidebar({ activeNav, onNav, facilityName, navItems }) {
  return (
    <aside style={{
      position: "sticky",
      top: 0,
      left: 0,
      width: `${SIDEBAR_W}px`,
      flexShrink: 0,
      minHeight: "calc(100vh - 140px)",
      alignSelf: "stretch",
      overflowY: "auto",
      background: C.sidebarBg,
      color: IPD_COLORS.white,
      display: "flex",
      flexDirection: "column",
      padding: "20px 12px",
      borderRight: `1px solid ${IPD_SIDEBAR.border}`,
      boxSizing: "border-box",
      zIndex: 100,
    }}>
      {/* Brand */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "4px 8px 14px", marginBottom: "4px", borderBottom: `1px solid ${IPD_SIDEBAR.border}`, flexShrink: 0 }}>
        <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "#1a4a7a" /* one-off brand accent for logo tile, no IPD_COLORS match */, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <SvgIcon name="ai" size={17} color={IPD_COLORS.white} />
        </div>
        <div>
          <div style={{ fontSize: "15px", fontWeight: "800", color: IPD_COLORS.white }}>HTMATE</div>
          <div style={{ fontSize: "10px", fontWeight: "600", color: IPD_SIDEBAR.text, letterSpacing: "0.05em" }}>IPD BILLING · HMIS</div>
        </div>
      </div>

      {/* Facility */}
      <div style={{ padding: "8px 10px 12px", borderBottom: `1px solid ${IPD_SIDEBAR.border}`, marginBottom: "8px", flexShrink: 0 }}>
        <div style={{ fontSize: "9px", fontWeight: "700", color: IPD_SIDEBAR.label, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "3px" }}>Facility</div>
        <div style={{ fontSize: "12px", fontWeight: "600", color: IPD_SIDEBAR.text }}>{facilityName}</div>
      </div>

      <div style={{ fontSize: "9px", fontWeight: "700", color: IPD_SIDEBAR.label, textTransform: "uppercase", letterSpacing: "0.08em", padding: "0 8px", marginBottom: "6px", flexShrink: 0 }}>
        Workspace
      </div>

      {/* Nav */}
      <nav style={{ display: "flex", flexDirection: "column", gap: "2px", flexShrink: 0 }}>
        {navItems.map(item => {
          const active  = activeNav === item.id;
          const noRoute = !item.route;
          return (
            <button
              key={item.id}
              onClick={() => onNav(item)}
              title={noRoute ? "Coming soon" : item.label}
              className={`ipd-nav-btn${active ? " ipd-nav-active" : ""}${noRoute ? " ipd-nav-disabled" : ""}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "9px 12px",
                borderRadius: "7px",
                width: "100%",
                background: active ? C.sidebarActive : "transparent",
                color: active ? IPD_COLORS.white : noRoute ? DISABLED_NAV_COLOR : IPD_SIDEBAR.text,
                border: "none",
                fontSize: "13px",
                fontWeight: "600",
                textAlign: "left",
                cursor: noRoute ? "not-allowed" : "pointer",
                opacity: noRoute ? 0.6 : 1,
                outline: "none",
              }}
            >
              <SvgIcon name={item.icon} size={16} color={active ? IPD_COLORS.white : noRoute ? DISABLED_NAV_COLOR : IPD_SIDEBAR.text} />
              {item.label}
              {noRoute && (
                <span style={{ marginLeft: "auto", fontSize: "9px", color: DISABLED_NAV_COLOR, fontWeight: "600" }}>
                  SOON
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* AI Copilot — always at bottom */}
      {/* <div
        className="ipd-copilot"
        style={{ marginTop: "auto", background: "#071828", border: "1px solid #1e3a5f", borderRadius: "10px", padding: "12px 14px", cursor: "pointer", flexShrink: 0 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "4px" }}>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#22d3ee", animation: "ipdPulse 2s infinite" }} />
          <span style={{ color: "#22d3ee", fontSize: "10px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase" }}>AI Copilot</span>
        </div>
        <div style={{ color: "#fff", fontSize: "12px", fontWeight: "700" }}>3 anomalies detected today</div>
        <div style={{ color: "#7eb3d8", fontSize: "10px", lineHeight: "1.4", marginTop: "2px" }}>Tap to review charges &amp; risk alerts.</div>
      </div> */}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT WRAPPER — used by every IPD billing page
// ─────────────────────────────────────────────────────────────────────────────
// `navIds` narrows the sidebar to the listed NAV ids — used by entry points that
// expose a single workspace (e.g. the TPA tile, which only gets Insurance & Claims).
// Omit it to keep the full billing nav.
export default function IPDBillingLayout({ activeNav, navIds, children }) {
  const history = useHistory();
  const { canAny, loaded } = useBillingPermissions();

  const facilityName = sessionStorage.getItem("facility_name") || "Your Facility";

  // Permission-gated nav items (e.g. Approvals) are FAIL-CLOSED: shown only once we
  // have a definitive permission list that grants access. This guarantees a Cashier
  // never sees Approvals — not during the permission fetch, and not if it fails.
  const navItems = NAV.filter(
    (item) =>
      (!navIds || navIds.includes(item.id)) &&
      (!item.requiresAny || (loaded && canAny(item.requiresAny))),
  );

  useEffect(() => {
    if (!sessionStorage.getItem("accessToken")) history.push("/login");
  }, [history]);

  // Scroll to top on page navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeNav]);

  // Clicking the item you're already on is a no-op. Beyond being pointless, it
  // would push a narrowed entry point (e.g. /tpa) back onto its full-sidebar
  // route (/ipd-billing/insurance) and widen the nav the caller restricted.
  const handleNav = (item) => {
    if (item.route && item.id !== activeNav) history.push(item.route);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      <style>{`
        @keyframes ipdPulse   { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes ipdShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes ipdFadeIn  { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }

        .ipd-nav-btn { transition: background 0.12s; }
        .ipd-nav-btn:focus { outline: none; }
        .ipd-nav-btn:focus-visible { outline: 2px solid rgba(126,179,216,0.4); outline-offset: -2px; }
        .ipd-nav-btn:not(.ipd-nav-active):not(.ipd-nav-disabled):hover { background: ${C.sidebarHover} !important; }

        .ipd-copilot { transition: background 0.12s; }
        .ipd-copilot:hover { background: ${C.primary} !important; }
      `}</style>

      <Sidebar activeNav={activeNav} onNav={handleNav} facilityName={facilityName} navItems={navItems} />

      <div style={{ display: "flex", flexDirection: "column", minHeight: "calc(100vh - 140px)", background: C.bg, flex: 1, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}
